import { useState, useEffect } from 'react'
import { ReviewFeed } from '@/components/reviews/review-feed'
import { AIResponsePanel } from '@/components/reviews/ai-response-panel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { fetchInternalLocations, type Location } from '@/lib/api/locations'
import type { Review } from '@/lib/api/reviews'
import { MapPin, AlertCircle, Bot } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useLocationPersistence } from '@/hooks/use-location-persistence'
import { getLocationLabel } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export function ReviewsPage() {
  const { t } = useTranslation()
  const [locations, setLocations] = useState<Location[]>([])
  const [selectedReview, setSelectedReview] = useState<Review | null>(null)
  const [generatingReviewId, setGeneratingReviewId] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Persist selected location in localStorage
  const locationIds = locations.map((l) => l.id)
  const { selectedLocationId, setSelectedLocationId } = useLocationPersistence(locationIds)

  useEffect(() => {
    async function loadLocations() {
      try {
        setIsLoading(true)
        const fetchedLocations = await fetchInternalLocations()
        setLocations(fetchedLocations)
        // Location persistence hook handles auto-selection
      } catch (err) {
        setError(err instanceof Error ? err.message : t('reviews.errorLoadLocations'))
      } finally {
        setIsLoading(false)
      }
    }
    loadLocations()
  }, [])

  const handleReviewSelect = (review: Review) => {
    setSelectedReview(review)
    setIsPanelOpen(true)
  }

  const handleGenerateResponse = (review: Review) => {
    setSelectedReview(review)
    setGeneratingReviewId(review.id)
    setIsPanelOpen(true)
  }

  const handleGenerateTriggered = (reviewId: number, isLoading: boolean) => {
    setGeneratingReviewId(isLoading ? reviewId : undefined)
  }

  const handleDraftGenerated = (reviewId: number) => {
    if (selectedReview?.id === reviewId) {
      setSelectedReview((prev) => (prev ? { ...prev, has_draft: true } : null))
    }
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleDraftSaved = (reviewId: number) => {
    if (selectedReview?.id === reviewId) {
      setSelectedReview((prev) => (prev ? { ...prev, has_draft: true } : null))
    }
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleLocationChange = (value: string) => {
    setSelectedLocationId(parseInt(value))
    setSelectedReview(null)
    setIsPanelOpen(false)
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t('reviews.failedLoadLocationsTitle')}</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
      </div>
    )
  }

  if (!isLoading && locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <MapPin className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold mb-2">{t('reviews.noLocationsTitle')}</h2>
        <p className="text-muted-foreground mb-6 max-w-md">{t('reviews.noLocationsDescription')}</p>
        <Button asChild>
          <Link to="/locations/search">
            <MapPin className="h-4 w-4" />
            {t('reviews.addFirstLocation')}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t('reviews.title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{t('reviews.subtitle')}</p>
        </div>

        {/* Location Selector */}
        {isLoading ? (
          <Skeleton className="h-9 w-full sm:w-64" />
        ) : (
          <Select value={selectedLocationId?.toString()} onValueChange={handleLocationChange}>
            <SelectTrigger className="w-full sm:w-72">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder={t('reviews.selectLocation')} />
            </SelectTrigger>
            <SelectContent>
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id.toString()}>
                  {getLocationLabel(location)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Main Content - Full Width */}
      <div className="w-full">
        {isLoading || !selectedLocationId ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-4 border border-border/60 rounded-xl space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-4/5" />
              </div>
            ))}
          </div>
        ) : (
          <ReviewFeed
            locationId={selectedLocationId}
            selectedReviewId={selectedReview?.id}
            generatingReviewId={generatingReviewId}
            refreshTrigger={refreshTrigger}
            onReviewSelect={handleReviewSelect}
            onGenerateResponse={handleGenerateResponse}
          />
        )}
      </div>

      {/* AI Response Panel - Side Sheet */}
      <Sheet open={isPanelOpen} onOpenChange={setIsPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
          <SheetHeader className="p-6 pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              {t('reviews.responseSheetTitle')}
            </SheetTitle>
            <SheetDescription>{t('reviews.responseSheetDescription')}</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            <AIResponsePanel
              selectedReview={selectedReview}
              onGenerateTriggered={handleGenerateTriggered}
              onDraftGenerated={handleDraftGenerated}
              onDraftSaved={handleDraftSaved}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
